import { FC, Suspense, useCallback } from 'react';

import { graphql, loadQuery } from 'react-relay';
import { IEnvironment } from 'relay-runtime';
import { Link } from 'react-router-dom';

import {
  createClientMutationId,
  timeAgo,
  useRouteData,
  useToggle,
} from 'shared-lib';
import {
  BooleanActionItem,
  Button,
  DescriptionItem,
  DescriptionList,
  EdSkeleton,
  EdTable,
  Size,
} from 'shared-ui';

import { ElevateAccountTypes } from '../../../authn';
import { IdentityUserChangeEmail } from './IdentityUserChangeEmail';
import { IdentityUserChangePassword } from './IdentityUserChangePassword';
import {
  identityAccountColumns,
  IdentityAccountRow,
} from './IdentityAccountRow';
import {
  IdentityUserAddAccountTypeInput,
  useIdentityUserAddAccountType,
  IdentityUserRemoveAccountTypeInput,
  useIdentityUserRemoveAccountType,
} from '../mutations';

import { IdentityUserDetailsRouteQuery } from './__generated__/IdentityUserDetailsRouteQuery.graphql';

export const queryDef = graphql`
  query IdentityUserDetailsRouteQuery($identityUserCode: Long!) {
    identityUser: identityUserByCode(code: $identityUserCode) {
      __id
      id
      code
      identityUserId
      lastLoggedIn
      updatedAt
      createdAt
      firstName
      lastName
      email
      accountTypes
      adminAccounts {
        accountId
        ...IdentityAccountRow_account
      }
      candidateAccounts {
        accountId
        ...IdentityAccountRow_account
      }
      employerAccounts {
        accountId
        ...IdentityAccountRow_account
      }
    }
  }
`;

export async function loader(environment: IEnvironment, params: any) {
  const code = params.identityUserCode as string;
  const identityUserCode = parseInt(code);

  try {
    return loadQuery<IdentityUserDetailsRouteQuery>(environment, queryDef, {
      identityUserCode,
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
}

const AccountTypeEditor: FC<{
  identityUserId: string;
  label: string;
  accountType: ElevateAccountTypes;
  hasAccountType: boolean;
  canRemove: boolean;
}> = ({ identityUserId, label, accountType, hasAccountType, canRemove }) => {
  const [remove, isRemoving] = useIdentityUserRemoveAccountType();
  const onRemoveAccountType = useCallback(
    async (accountType: string) => {
      const input: IdentityUserRemoveAccountTypeInput = {
        identityUserId,
        accountType: accountType,
        clientMutationId: createClientMutationId(),
      };

      await remove({ input });
    },
    [remove],
  );

  const [add, isAdding] = useIdentityUserAddAccountType();
  const onAddAccountType = useCallback(
    async (accountType: string) => {
      const input: IdentityUserAddAccountTypeInput = {
        identityUserId,
        accountType: accountType,
        clientMutationId: createClientMutationId(),
      };

      await add({ input });
    },
    [add],
  );

  let action: JSX.Element;

  if (hasAccountType && canRemove) {
    action = (
      <Button
        color='warning'
        size={Size.md}
        disabled={isAdding || isRemoving}
        clicked={() => onRemoveAccountType(accountType)}
      >
        Remove
      </Button>
    );
  } else if (hasAccountType && !canRemove) {
    action = (
      <div className='text-sm text-gray-500'>Cannot Remove Account Type</div>
    );
  } else {
    action = (
      <Button
        color='primary'
        size={Size.md}
        disabled={isAdding || isRemoving}
        clicked={() => onAddAccountType(accountType)}
      >
        Add
      </Button>
    );
  }

  return (
    <BooleanActionItem label={label} value={hasAccountType} action={action} />
  );
};

export const IdentityUserDetails: FC<{
  routeId: string;
}> = ({ routeId }) => {
  const [isEditingEmail, toggleIsEditingEmail] = useToggle();
  const [isEditingPassword, toggleIsEditingPassword] = useToggle();
  const [remove, isRemoving] = useIdentityUserRemoveAccountType();
  const data = useRouteData<IdentityUserDetailsRouteQuery>(queryDef, routeId);
  const onRemoveAllAccountTypes = useCallback(
    async (accountType: string, identityUserId: string) => {
      const input: IdentityUserRemoveAccountTypeInput = {
        identityUserId,
        accountType: accountType,
        clientMutationId: createClientMutationId(),
      };

      await remove({ input });
    },
    [remove],
  );

  const handleDisableAccount = useCallback(
    async (
      identityUserId: string,
      hasCandidateAccountType: boolean,
      hasEmployerAccountType: boolean,
      hasInternalAccountType: boolean,
    ) => {
      if (hasCandidateAccountType) {
        await onRemoveAllAccountTypes(
          ElevateAccountTypes?.Contractor,
          identityUserId,
        );
      }
      if (hasEmployerAccountType) {
        await onRemoveAllAccountTypes(
          ElevateAccountTypes?.Employer,
          identityUserId,
        );
      }
      if (hasInternalAccountType) {
        await onRemoveAllAccountTypes(
          ElevateAccountTypes?.Internal,
          identityUserId,
        );
      }
    },
    [onRemoveAllAccountTypes],
  );

  if (!data) {
    return <>Loading...</>;
  }

  const { identityUser } = data;

  if (!identityUser) {
    return <>Loading...</>;
  }

  const allAccounts = [
    ...identityUser.adminAccounts,
    ...identityUser.employerAccounts,
    ...identityUser.candidateAccounts,
  ];

  const accountRows = allAccounts.map(d => {
    return <IdentityAccountRow key={d.accountId} fragmentRef={d} />;
  });

  const hasInternalAccountType = identityUser.accountTypes.some(
    a => a === 'INTERNAL',
  );
  const hasEmployerAccountType = identityUser.accountTypes.some(
    a => a === 'EMPLOYER',
  );
  const hasCandidateAccountType = identityUser.accountTypes.some(
    a => a === 'CANDIDATE',
  );

  const hasAccountType =
    hasCandidateAccountType || hasEmployerAccountType || hasInternalAccountType;

  return (
    <Suspense fallback={<EdSkeleton />}>
      <div className='m-8'>
        <div className='mb-2 flex items-center justify-between gap-2 bg-white p-4 shadow sm:rounded-lg'>
          <div className='flex flex-col gap-2'>
            <div className='text-primary-500 px-4 text-lg font-medium'>
              {identityUser.email}
            </div>
            <div className='text-primary-500 px-4 text-sm'>
              Updated {timeAgo(identityUser.updatedAt)}
            </div>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='text-primary-500 px-4 text-lg font-medium'>
              Account Status:{' '}
              <span
                className={
                  hasAccountType ? 'text-green-600' : 'text-danger-600'
                }
              >
                {hasAccountType ? 'Active' : 'Disabled'}
              </span>
            </div>
            <div className='flex justify-end  px-4'>
              {hasAccountType && (
                <Button
                  color='warning'
                  size={Size.md}
                  clicked={() =>
                    hasAccountType &&
                    handleDisableAccount(
                      identityUser.identityUserId,
                      hasCandidateAccountType,
                      hasEmployerAccountType,
                      hasInternalAccountType,
                    )
                  }
                >
                  Disable
                </Button>
              )}
            </div>
          </div>
        </div>
        <DescriptionList
          heading={
            <div className='flex space-x-2'>
              <span className='text-info-600'>Account Details</span>
            </div>
          }
          items={
            <>
              {isEditingEmail ? (
                <div className='my-4 flex flex-col gap-2'>
                  <IdentityUserChangeEmail
                    identityUserId={identityUser.identityUserId}
                    onDone={toggleIsEditingEmail}
                    onCancel={toggleIsEditingEmail}
                  />
                </div>
              ) : (
                <DescriptionItem label='Email'>
                  <div className='flex items-center gap-2'>
                    <div>{identityUser.email}</div>
                    <div>
                      <Button color={'primary'} clicked={toggleIsEditingEmail}>
                        Edit
                      </Button>
                    </div>
                  </div>
                </DescriptionItem>
              )}
              {isEditingPassword ? (
                <div className='my-4 flex flex-col gap-2'>
                  <IdentityUserChangePassword
                    identityUserId={identityUser.identityUserId}
                    onDone={toggleIsEditingPassword}
                    onCancel={toggleIsEditingPassword}
                  />
                </div>
              ) : (
                <DescriptionItem label='Password'>
                  <div className='flex items-center gap-2'>
                    <div>****</div>
                    <div>
                      <Button
                        color={'primary'}
                        clicked={toggleIsEditingPassword}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                </DescriptionItem>
              )}
            </>
          }
        />

        <div className='mt-8 bg-white p-4 shadow sm:w-1/2 sm:rounded-lg'>
          <div className='mb-4 flex space-x-2'>
            <span className='text-info-600'>Account Types</span>
          </div>

          <AccountTypeEditor
            identityUserId={identityUser.identityUserId}
            label='Internal'
            accountType={ElevateAccountTypes.Internal}
            canRemove={identityUser.adminAccounts.length === 0}
            hasAccountType={hasInternalAccountType}
          />

          <AccountTypeEditor
            identityUserId={identityUser.identityUserId}
            label='Employer'
            accountType={ElevateAccountTypes.Employer}
            canRemove={identityUser.employerAccounts.length === 0}
            hasAccountType={hasEmployerAccountType}
          />

          <AccountTypeEditor
            identityUserId={identityUser.identityUserId}
            label='Candidate'
            accountType={ElevateAccountTypes.Contractor}
            canRemove={identityUser.candidateAccounts.length === 0}
            hasAccountType={hasCandidateAccountType}
          />
        </div>

        <div className='mt-8'>
          <div className='mb-4 flex space-x-2'>
            <span className='text-info-600'>Accounts</span>
          </div>
          <EdTable defaultColumns={identityAccountColumns} rows={accountRows} />
        </div>
      </div>
    </Suspense>
  );
};

export const createRoute = (routePrefix: string, environment: IEnvironment) => {
  const routeId = `${routePrefix}-identity-user-details`;

  return {
    id: routeId,
    path: 'details',
    element: <IdentityUserDetails routeId={routeId} />,
    handle: {
      crumb: () => <Link to='details'>Details</Link>,
    },
    loader: ({ params }) => {
      return loader(environment, params);
    },
  };
};
